import { Box, Container, GridItem, SimpleGrid } from '@chakra-ui/react';
import { GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { FormattedMessage } from 'react-intl';

import { withCommonServerSideProps } from '@/lib/withCommonServerSideProps';

import Article from '@/components/Article';
import ContactUserForm from '@/components/Contact/ContactUserForm';
import { H1, H2 } from '@/components/Heading';
import Seo from '@/components/Seo';

import { LocaleProps } from '@/models/props/LocalizationProps';
import { ContactService } from '@/services/ContactService';

type ContactProps = {
    content: Record<string, string>;
};

const ContactPage: NextPage<ContactProps> = (props: ContactProps) => {
    const { locale } = useRouter();
    const content = props.content;

    return (
        <>
            <Seo templateTitle={content.title} />
            <GoogleReCaptchaProvider
                reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY as string}
                language={locale}
                scriptProps={{
                    async: false,
                    defer: false,
                    appendTo: 'head',
                    nonce: undefined
                }}
            >
                <Container maxW="container.lg" pb={6}>
                    <Article>
                        <H1 textAlign="center">{content.title}</H1>
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                            <GridItem as="section" display="flex" flexDirection="column">
                                <Box
                                    border="1px"
                                    borderColor="blue.200"
                                    borderRadius="1.625rem"
                                    mb={{ base: 3, md: 4 }}
                                    p={6}
                                    h={{ base: 'auto', md: 'calc(50% - 1rem)' }}
                                >
                                    <H2
                                        size="h3"
                                        fontWeight={{ base: 'normal', md: 'semibold' }}
                                        mb={5}
                                    >
                                        <FormattedMessage id="contact.company-data.title" />
                                    </H2>
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: content.section1
                                        }}
                                    />
                                </Box>
                                <Box
                                    border="1px"
                                    borderColor="blue.200"
                                    borderRadius="1.625rem"
                                    p={6}
                                    h={{ base: 'auto', md: '50%' }}
                                >
                                    <H2
                                        size="h3"
                                        fontWeight={{ base: 'normal', md: 'semibold' }}
                                        mb={5}
                                    >
                                        <FormattedMessage id="contact.contact-data.title" />
                                    </H2>
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: content.section2
                                        }}
                                    />
                                </Box>
                            </GridItem>
                            <GridItem as="section">
                                <Box
                                    border="1px"
                                    borderColor="blue.200"
                                    borderRadius="1.625rem"
                                    h="100%"
                                    p={6}
                                >
                                    <ContactUserForm />
                                </Box>
                            </GridItem>
                        </SimpleGrid>
                    </Article>
                </Container>
            </GoogleReCaptchaProvider>
        </>
    );
};

export const getServerSideProps = withCommonServerSideProps(
    async (context: GetServerSidePropsContext) => {
        const { locale } = context;
        let content;

        try {
            content = await ContactService.getContact(locale as LocaleProps);
        } catch {
            content = [];
        }

        if (content.length === 0) {
            return {
                notFound: true
            };
        }

        return {
            props: {
                content
            }
        };
    }
);

export default ContactPage;
